import { Bomb, ChevronDown, ChevronUp, List, Pause, Play, X, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getDraftBoardPosColor } from '@/lib/nflDraftBoardColors';
import type { FeedEvent, FeedFilter } from '@/lib/nflDraftFeed';
import type { RailTab } from '@/lib/nflDraftRoomPrefs';

/**
 * Prompt 4: the right rail -- QUEUE / FEED / CHAT.
 *
 * MUTUALLY EXCLUSIVE. Exactly one panel renders at a time and fills the column. The current
 * production build stacks the queue above the pick log; that is what this replaces.
 *
 * shadcn Tabs is deliberately NOT used here. Its TabsContent keeps every panel mounted and
 * toggles visibility, which would keep three scroll regions and a live chat subscription
 * alive at once inside a fixed-height column. A single discriminated render is both closer
 * to the design's "exactly one panel" and cheaper.
 */

export interface QueueRow {
  playerId: string;
  name: string;
  position: string;
  projection?: number | null;
  /** ADP says they will not survive to the user's next pick. */
  atRisk?: boolean;
}

export interface RightRailProps {
  tab: RailTab;
  onTabChange: (tab: RailTab) => void;
  queueCount: number;
  unread: { chat: number; feed: number };

  // Queue
  queue: QueueRow[];
  onQueueMove: (from: number, to: number) => void;
  onQueueRemove: (playerId: string) => void;
  /**
   * SAB-996 item 2: draft the queued player directly, Sleeper/ESPN style.
   *
   * Offered on the TOP row only, and only while on the clock. The top row is the one
   * nfl-auto-pick would take if the clock expired, so "Draft" here does deliberately what
   * waiting would do accidentally -- it does not introduce a second, competing notion of
   * which queued player is next.
   */
  onDraftFromQueue?: (playerId: string) => void;
  /** True while this team is on the clock. Absent means never show the action. */
  isMyTurn?: boolean;
  /** A pick is in flight; prevents a double submit. */
  isPicking?: boolean;
  // Feed
  feed: FeedEvent[];
  feedFilter: FeedFilter;
  onFeedFilterChange: (f: FeedFilter) => void;

  // Chat is rendered by the caller (DraftChatLive owns its own subscription).
  chatPanel?: React.ReactNode;
}

const TABS: { id: RailTab; label: string }[] = [
  { id: 'queue', label: 'QUEUE' },
  { id: 'feed', label: 'FEED' },
  { id: 'chat', label: 'CHAT' },
];

export default function RightRail(props: RightRailProps) {
  const { tab, onTabChange, queueCount, unread } = props;

  return (
    <section
      data-testid="right-rail"
      /* SAB-879: min-w-0 as well as min-h-0. SAB-877 made the cell a `grid` so this
         section would stretch vertically; that also made it a GRID ITEM, whose automatic
         minimum size is its min-content -- so a single queue row (min-content 311-410px
         depending on the name) pushed the section past its fixed 322px cell and shoved the
         CHAT tab off screen. min-w-0 clamps the item to its track; the name span's existing
         `truncate` then engages and does the rest. */
      className="min-h-0 min-w-0 flex flex-col border-l border-border bg-card"
    >
      <div className="flex-none flex border-b border-border" role="tablist">
        {TABS.map(t => {
          const active = tab === t.id;
          const dot = (t.id === 'chat' && unread.chat > 0) || (t.id === 'feed' && unread.feed > 0);
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              data-testid={`rail-tab-${t.id}`}
              onClick={() => onTabChange(t.id)}
              className={cn(
                'relative flex-1 py-[11px] font-display text-[11.5px] font-bold',
                active ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground',
              )}
            >
              {t.label}
              {t.id === 'queue' && <span className="ml-1 opacity-70 tabular-nums">{queueCount}</span>}
              {dot && (
                <span
                  data-testid={`unread-${t.id}`}
                  className="absolute right-3 top-2 h-1.5 w-1.5 rounded-full bg-damage"
                />
              )}
            </button>
          );
        })}
      </div>

      {tab === 'queue' && <QueuePanel {...props} />}
      {tab === 'feed' && <FeedPanel {...props} />}
      {tab === 'chat' && (
        <div data-testid="rail-chat" className="flex-1 min-h-0 flex flex-col">
          {props.chatPanel}
        </div>
      )}
    </section>
  );
}

/** Exported for SAB-871: the mobile room reuses this verbatim inside its queue sheet,
 *  rather than growing a second queue implementation free to drift from this one. */
export function QueuePanel({ queue, onQueueMove, onQueueRemove, onDraftFromQueue, isMyTurn, isPicking }: RightRailProps) {
  return (
    <div data-testid="queue-panel" className="flex-1 min-h-0 flex flex-col gap-1.5 p-3">
      <div className="flex-none flex items-center justify-between">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
          Your Queue
        </span>
      </div>

      {queue.length === 0 ? (
        <div
          data-testid="queue-empty"
          className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-border text-center"
        >
          <List className="h-5 w-5 text-muted-foreground" />
          <span className="px-6 text-[11.5px] text-muted-foreground">
            Queue players from the board. Auto-picks take them in order.
          </span>
        </div>
      ) : (
        <ol className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1.5">
          {queue.map((q, i) => {
            const c = getDraftBoardPosColor(q.position);
            return (
              <li
                key={q.playerId}
                data-testid="queue-entry"
                data-player-id={q.playerId}
                data-at-risk={q.atRisk || undefined}
                className={cn(
                  'flex items-center gap-2 rounded-[9px] border px-2.5 py-2',
                  q.atRisk ? 'border-damage/25 bg-damage/[0.07]' : 'border-border bg-muted',
                )}
              >
                <span className="w-3 flex-none font-mono text-[11px] font-bold text-muted-foreground">
                  {i + 1}
                </span>
                <span
                  className={cn(
                    'inline-flex h-[18px] w-8 flex-none items-center justify-center rounded border font-display text-[9px] font-bold',
                    c.text, c.border, c.bgFaint,
                  )}
                >
                  {q.position}
                </span>
                <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-white">
                  {q.name}
                </span>
                {q.atRisk ? (
                  <span className="font-display text-[9.5px] font-bold text-damage">AT RISK</span>
                ) : (
                  <span className="font-display text-xs font-bold tabular-nums text-damage">
                    {q.projection != null ? q.projection.toFixed(1) : '—'}
                  </span>
                )}
                {/* SAB-996: Draft, top row only, only on the clock. Rendered before the
                    move controls so the primary action sits nearest the name rather than
                    at the end of a row of icon buttons. */}
                {i === 0 && isMyTurn && onDraftFromQueue && (
                  <Button
                    size="sm"
                    aria-label={`Draft ${q.name}`}
                    disabled={isPicking}
                    onClick={() => onDraftFromQueue(q.playerId)}
                    className="h-6 flex-none px-2 text-[10px] font-bold uppercase tracking-wider"
                  >
                    Draft
                  </Button>
                )}
                {/* Move up/down rather than drag: there is no DnD library in this repo and
                    this run may not add a dependency. reorder(from,to) is index-based, so a
                    drag layer can be added later without touching persistence. */}
                <span className="flex flex-none items-center">
                  <Button
                    variant="ghost" size="icon" aria-label={`Move ${q.name} up`}
                    disabled={i === 0} onClick={() => onQueueMove(i, i - 1)}
                    className="h-6 w-6"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" aria-label={`Move ${q.name} down`}
                    disabled={i === queue.length - 1} onClick={() => onQueueMove(i, i + 1)}
                    className="h-6 w-6"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" aria-label={`Remove ${q.name} from queue`}
                    onClick={() => onQueueRemove(q.playerId)}
                    className="h-6 w-6 text-muted-foreground"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </span>
              </li>
            );
          })}
        </ol>
      )}

    </div>
  );
}

const FILTERS: { id: FeedFilter; label: React.ReactNode }[] = [
  { id: 'all', label: 'ALL' },
  { id: 'picks', label: 'PICKS' },
  { id: 'sabotage', label: <Bomb className="h-3 w-3" /> },
];

const EVENT_STYLE: Record<string, string> = {
  commissioner: 'border-l-gold bg-gold/[0.08]',
  sabotage: 'border-l-damage bg-damage/[0.08]',
  autopick_switch: 'border-l-[hsl(220_90%_56%)] bg-[hsl(220_90%_56%/0.07)]',
};

/** Exported for SAB-871: the mobile CHAT tab folds the feed in as a segmented control. */
export function FeedPanel({ feed, feedFilter, onFeedFilterChange }: RightRailProps) {
  return (
    <div data-testid="feed-panel" className="flex-1 min-h-0 flex flex-col p-3">
      <div className="flex-none flex items-center justify-end gap-1.5 pb-2">
        {FILTERS.map(f => (
          <button
            key={f.id}
            data-testid={`feed-filter-${f.id}`}
            aria-pressed={feedFilter === f.id}
            onClick={() => onFeedFilterChange(f.id)}
            className={cn(
              'rounded-full px-2.5 py-[3px] font-display text-[9.5px] font-bold',
              feedFilter === f.id ? 'bg-primary text-[#12130f]' : 'bg-muted text-muted-foreground',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1.5">
        {feed.length === 0 && (
          <div className="py-8 text-center text-[11.5px] text-muted-foreground">Nothing yet.</div>
        )}
        {feed.map(e => {
          if (e.kind === 'pick') {
            const c = getDraftBoardPosColor(e.position ?? '');
            return (
              <div
                key={e.id}
                data-testid="feed-row"
                data-kind="pick"
                className={cn(
                  // items-start, not items-center: the row is two lines now, and the pick
                  // number, position badge and AUTO flag belong beside the PLAYER on the
                  // first line rather than floating against the middle of the pair.
                  'flex items-start gap-2 rounded-lg px-2 py-[7px]',
                  e.isMine && 'border-l-2 border-l-primary bg-primary/[0.07]',
                )}
              >
                <span className="w-6 flex-none font-mono text-[10.5px] font-bold text-muted-foreground">
                  {e.pickNumber}
                </span>
                <span
                  className={cn(
                    'inline-flex h-[17px] w-8 flex-none items-center justify-center rounded border font-display text-[9px] font-bold',
                    c.text, c.border, c.bgFaint,
                  )}
                >
                  {e.position}
                </span>
                {/*
                  SAB-1018: the player and the team are STACKED, and the pair owns the
                  remaining width as one min-w-0 column.

                  Before, they were siblings competing on one line: the player had
                  `min-w-0 flex-1` and the team had `flex-none`. So the team could not
                  shrink at all and the player absorbed every pixel of pressure. Measured in
                  a 321px feed panel: "The Absolutely Enormous Team Name Brigade" took 231px
                  and the player name next to it rendered at width ZERO -- gone, not
                  ellipsised. Across a sample of 12 rows the team name truncated exactly
                  never, and 7 player names were cut.

                  The player is the information and the team is context, so the player no
                  longer competes for width at all: it gets the full column and the team
                  truncates on its own line underneath. Two lines rather than one-line
                  team-truncation because the panel is only ~321px -- with a long team name
                  sharing the line, even ordinary player names would have to abbreviate to
                  fit, and abbreviating when there is room is a loss.
                */}
                <span className="min-w-0 flex-1 leading-tight">
                  <span
                    data-testid="feed-player"
                    className="block truncate text-xs font-semibold text-white"
                  >
                    {e.playerName}
                  </span>
                  <span
                    data-testid="feed-team"
                    className="block truncate text-[10.5px] text-muted-foreground"
                  >
                    {e.teamName}
                  </span>
                </span>
                {e.auto && (
                  <span className="flex-none rounded bg-[hsl(220_90%_56%/0.18)] px-1 py-px font-display text-[8px] font-bold text-[#7EB0F7]">
                    AUTO
                  </span>
                )}
              </div>
            );
          }

          if (e.kind === 'chat') {
            return (
              <div key={e.id} data-testid="feed-row" data-kind="chat" className="rounded-lg bg-muted p-2">
                <div className="flex items-center gap-1.5">
                  <span className={cn('text-xs font-bold', e.isMine ? 'text-primary' : 'text-[#c9cdd6]')}>
                    {e.authorName}
                  </span>
                </div>
                <div className="text-[11.5px] text-[#c9cdd6]">{e.body}</div>
              </div>
            );
          }

          const Icon = e.kind === 'commissioner'
            ? (e.title.includes('paused') ? Pause : Play)
            : e.kind === 'sabotage' ? Bomb : Zap;

          return (
            <div
              key={e.id}
              data-testid="feed-row"
              data-kind={e.kind}
              className={cn('rounded-lg border-l-2 p-2', EVENT_STYLE[e.kind] ?? 'border-l-border')}
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold text-white">
                <Icon className="h-3 w-3 flex-none" />
                <span className="truncate">{e.title}</span>
              </div>
              {e.meta && <div className="text-[10.5px] text-muted-foreground">{e.meta}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
